/**
 * Left-hand vertical tool rail (Miro-style): Select / Comment / Draw, the
 * board's mutually-exclusive capture modes (tool-mode.ts). Replaces the old
 * Comment/Draw buttons that lived in the topbar — the rail is the ONE place
 * these tools live, and the ONE place their keyboard shortcuts are wired, so
 * a button click and a keypress can never disagree about what turns what off.
 *
 * Shortcuts — active only when nothing is capturing the keystroke as text
 * (core/keyboard.ts `isTypingTarget`) and the grid did not just consume it to
 * START editing a selected cell (GridTable's `onWrapperKeyDown` calls
 * `preventDefault()` for that case in the SAME event, before this bubble
 * listener runs — `event.defaultPrevented` is how F-50's existing "C" toggle
 * already detects it, and V/P now share that check):
 *  - V — select tool (Miro's "move" tool; always switches TO select, not a toggle)
 *  - C — comment mode (toggle; F-50)
 *  - P — draw/pen mode (toggle)
 * Esc is NOT handled here: each mode already returns to select on its own
 * (CommentOverlays' composer→popover→mode cascade; DrawingLayer's Escape) —
 * duplicating it here would just be a second listener racing the first.
 */
import { useEffect } from "react";
import { useStore } from "zustand";

import { isTypingTarget } from "../../core/keyboard.js";
import type { BoardSession } from "../../core/session.js";
import type { CommentsUiStore } from "../comments/store.js";
import type { DrawUiStore } from "../drawing/store.js";
import { activeTool, setActiveTool, type ToolMode } from "./tool-mode.js";

export interface ToolRailProps {
  session: BoardSession;
  commentsUi: CommentsUiStore;
  drawUi: DrawUiStore;
}

const svgProps = {
  width: 17,
  height: 17,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const iconSelect = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M3.5 2.5 12 6.8 8.3 8.1l2.4 3.6-1.5 1-2.4-3.6-2 2.8-1.3-9.4z" fill="currentColor" stroke="none" />
  </svg>
);

const iconComment = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M2.5 4.5A1.5 1.5 0 0 1 4 3h8a1.5 1.5 0 0 1 1.5 1.5v4.6A1.5 1.5 0 0 1 12 10.6H7.4l-2.9 2.9v-2.9H4a1.5 1.5 0 0 1-1.5-1.5V4.5z" />
  </svg>
);

const iconPen = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M3 13l.7-2.8 7.1-7.1a1.2 1.2 0 0 1 1.7 0l.4.4a1.2 1.2 0 0 1 0 1.7l-7.1 7.1L3 13z" />
    <path d="M9.6 4.3l2.1 2.1" />
  </svg>
);

export function ToolRail({ session, commentsUi, drawUi }: ToolRailProps) {
  const commentMode = useStore(commentsUi, (s) => s.mode);
  const drawMode = useStore(drawUi, (s) => s.mode);
  const frozen = useStore(session.store, (s) => s.frozen);
  const tool = activeTool(commentMode, drawMode);

  const select = (next: ToolMode) => setActiveTool({ commentsUi, drawUi }, next);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (key !== "v" && key !== "c" && key !== "p") return;
      if (session.store.getState().frozen) return;
      if (key === "v") {
        select("select");
        return;
      }
      const wanted: ToolMode = key === "c" ? "comment" : "draw";
      const current = activeTool(commentsUi.getState().mode, drawUi.getState().mode);
      select(current === wanted ? "select" : wanted);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- select/session/stores are stable for the view's life
  }, [session, commentsUi, drawUi]);

  return (
    <div className="tool-rail" role="toolbar" aria-label="Board tools" aria-orientation="vertical">
      <button
        type="button"
        className={`tool-rail-btn${tool === "select" ? " active" : ""}`}
        onClick={() => select("select")}
        data-tip="Select (V)"
        aria-pressed={tool === "select"}
      >
        {iconSelect}
      </button>
      <button
        type="button"
        className={`tool-rail-btn${tool === "comment" ? " active" : ""}`}
        onClick={() => select(tool === "comment" ? "select" : "comment")}
        disabled={frozen}
        data-tip="Comment — click anywhere to drop a comment (C)"
        aria-pressed={tool === "comment"}
      >
        {iconComment}
      </button>
      <button
        type="button"
        className={`tool-rail-btn${tool === "draw" ? " active" : ""}`}
        onClick={() => select(tool === "draw" ? "select" : "draw")}
        disabled={frozen}
        data-tip="Draw — drag to sketch a stroke; it belongs to the cell where it starts (P)"
        aria-pressed={tool === "draw"}
      >
        {iconPen}
      </button>
    </div>
  );
}
